import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Users } from 'lucide-react';
import { DepartmentVariableSettings } from '../../../services/api/departmentVariablesService';
import { DepartmentUser, categoryTitle } from '../utils/settingsView';
import CategoryCard from '../CategoryCard';
import AddCategoryForm from '../AddCategoryForm';
import SetupIntroduction, { WIZARD_STEPS } from './SetupIntroduction';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { EVENT_KEYS, trackLoad } from '../../../services/api/trackingService';

interface SetupWizardProps {
    settings: DepartmentVariableSettings;
    users: DepartmentUser[];
    onSaved: () => Promise<void>;
    onComplete: () => void;
}

export default function SetupWizard({
    settings,
    users,
    onSaved,
    onComplete,
}: SetupWizardProps) {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const [showIntroduction, setShowIntroduction] = useState(true);
    const [isAddingCategory, setIsAddingCategory] = useState(false);

    useEffect(() => {
        if (showIntroduction) return;
        trackLoad(EVENT_KEYS.wizardStep, { step: currentStep });
    }, [currentStep, showIntroduction]);

    if (showIntroduction) {
        return <SetupIntroduction onStart={() => setShowIntroduction(false)} />;
    }

    const step = WIZARD_STEPS[currentStep];
    const isLastStep = currentStep === WIZARD_STEPS.length - 1;
    const tab = step === 'departmentSettings' ? 'departmentInfo' : 'contacts';
    const tabCategories = settings.categories.filter(
        (category) => category.tab === tab
    );
    const stepCategories = tabCategories.filter((category) => {
        if (step === 'mriSafetyLeadership') {
            return category.key === 'mriSafetyLeadership';
        }
        if (step === 'departmentSettings') {
            return category.key === 'departmentInformation';
        }
        return category.key !== 'mriSafetyLeadership';
    });

    const goBack = () => {
        if (currentStep === 0) {
            setShowIntroduction(true);
            return;
        }
        setCurrentStep((s) => s - 1);
    };

    const goNext = () => {
        if (isLastStep) {
            onComplete();
            return;
        }
        setCurrentStep((s) => s + 1);
    };

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs text-muted-foreground">
                            {t('departmentVariables.wizard.setupProgress')}
                        </p>
                        <p className="text-sm font-medium">
                            {t(
                                `departmentVariables.wizard.steps.${step}.title`
                            )}
                        </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                        {t('departmentVariables.wizard.stepOf', {
                            current: currentStep + 1,
                            total: WIZARD_STEPS.length,
                        })}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {WIZARD_STEPS.map((wizardStep, i) => (
                        <div
                            key={wizardStep}
                            className={`h-2 flex-1 rounded-full transition-colors ${
                                i < currentStep
                                    ? 'bg-primary'
                                    : i === currentStep
                                      ? 'bg-primary/70'
                                      : 'bg-muted'
                            }`}
                        />
                    ))}
                </div>
            </div>

            <div className="space-y-3">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-semibold">
                            {t(
                                `departmentVariables.wizard.steps.${step}.title`
                            )}
                        </h2>
                        <p className="text-sm text-subtle-foreground font-medium">
                            {t(
                                `departmentVariables.wizard.steps.${step}.description`
                            )}
                        </p>
                    </div>
                    {step === 'yourContacts' && stepCategories.length > 0 && (
                        <Button
                            size="sm"
                            className="gap-1.5 shrink-0"
                            onClick={() => setIsAddingCategory(true)}
                        >
                            <Plus className="h-4 w-4" />
                            {t('departmentVariables.category.addCategory')}
                        </Button>
                    )}
                </div>
                {step === 'yourContacts' && isAddingCategory && (
                    <AddCategoryForm
                        tab="contacts"
                        takenTitles={tabCategories.map((category) =>
                            categoryTitle(category, t)
                        )}
                        onSaved={onSaved}
                        onClose={() => setIsAddingCategory(false)}
                    />
                )}
                {step === 'yourContacts' &&
                    stepCategories.length === 0 &&
                    !isAddingCategory && (
                        <Card className="border-subtle p-4 flex items-center gap-4">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-subtle-foreground">
                                <Users className="h-5 w-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <span className="font-semibold">
                                    {t('departmentVariables.wizard.emptyTitle')}
                                </span>
                                <p className="text-sm text-subtle-foreground font-medium">
                                    {t(
                                        'departmentVariables.wizard.emptyDescription'
                                    )}
                                </p>
                            </div>
                            <Button
                                size="sm"
                                className="gap-1.5 shrink-0"
                                onClick={() => setIsAddingCategory(true)}
                            >
                                <Plus className="h-4 w-4" />
                                {t('departmentVariables.category.addCategory')}
                            </Button>
                        </Card>
                    )}
                {stepCategories.map((category) => (
                    <CategoryCard
                        key={category.key}
                        category={category}
                        variables={settings.variables.filter(
                            (variable) => variable.category === category.key
                        )}
                        allVariables={settings.variables}
                        tabCategories={tabCategories}
                        users={users}
                        onSaved={onSaved}
                    />
                ))}
            </div>

            <div className="flex justify-between">
                <Button type="button" variant="outline" onClick={goBack}>
                    {t('departmentVariables.wizard.back')}
                </Button>
                <Button type="button" onClick={goNext}>
                    {isLastStep
                        ? t('departmentVariables.wizard.finishSetup')
                        : t('departmentVariables.wizard.next')}
                </Button>
            </div>
        </div>
    );
}
